// Shared Sentry helpers for internal-dashboard.
// Centralises scrubbing, capture rules, and dedupe logic so the same
// patterns apply everywhere we touch Sentry.
//
// See plan: sarahai-scratchpad/ai-notes/features/1-sentry-implementation/

import * as Sentry from "@sentry/nextjs";

const SERVICE_NAME = "internal-dashboard";

const SENSITIVE_HEADER_PATTERNS = [
  /^authorization$/i,
  /^cookie$/i,
  /^set-cookie$/i,
  /^x-.*-token$/i,
  /^x-api-key$/i,
];

const SENSITIVE_QUERY_PARAMS = new Set([
  "email",
  "phone",
  "token",
  "reset_token",
  "access_token",
  "refresh_token",
  "code",
  "state",
  "signature",
  "ssn",
  "nin",
  "password",
]);

const REDACTED = "[Filtered]";

const SENTRY_CAPTURED_FLAG = "__sentry_captured__";

type CapturedError = Error & { [SENTRY_CAPTURED_FLAG]?: boolean };

function markCaptured(error: unknown): void {
  if (error && typeof error === "object") {
    try {
      (error as CapturedError)[SENTRY_CAPTURED_FLAG] = true;
    } catch {
      // Frozen objects etc. — best-effort only.
    }
  }
}

function isCaptured(error: unknown): boolean {
  if (!error || typeof error !== "object") return false;
  return (error as CapturedError)[SENTRY_CAPTURED_FLAG] === true;
}

function scrubHeaders(
  headers: Record<string, string> | undefined,
): Record<string, string> | undefined {
  if (!headers) return headers;
  const out: Record<string, string> = {};
  for (const [key, value] of Object.entries(headers)) {
    if (SENSITIVE_HEADER_PATTERNS.some((re) => re.test(key))) {
      out[key] = REDACTED;
    } else {
      out[key] = value;
    }
  }
  return out;
}

function scrubQueryString(qs: string | undefined): string | undefined {
  if (!qs) return qs;
  try {
    const params = new URLSearchParams(qs);
    let mutated = false;
    for (const key of Array.from(params.keys())) {
      if (SENSITIVE_QUERY_PARAMS.has(key.toLowerCase())) {
        params.set(key, REDACTED);
        mutated = true;
      }
    }
    return mutated ? params.toString() : qs;
  } catch {
    return qs;
  }
}

function scrubUrl(url: string | undefined): string | undefined {
  if (!url) return url;
  try {
    const u = new URL(url, "http://placeholder.invalid");
    let mutated = false;
    for (const key of Array.from(u.searchParams.keys())) {
      if (SENSITIVE_QUERY_PARAMS.has(key.toLowerCase())) {
        u.searchParams.set(key, REDACTED);
        mutated = true;
      }
    }
    if (!mutated) return url;
    if (u.origin === "http://placeholder.invalid") {
      return `${u.pathname}${u.search}${u.hash}`;
    }
    return u.toString();
  } catch {
    return url;
  }
}

/**
 * Sentry beforeSend hook. Scrubs sensitive headers, URL/query params,
 * request bodies, and PII fields on the user object.
 *
 * Must never throw — wrap risky work in try/catch.
 */
export const beforeSend: NonNullable<Sentry.NodeOptions["beforeSend"]> = (event) => {
  try {
    if (event.request) {
      event.request.headers = scrubHeaders(
        event.request.headers as Record<string, string> | undefined,
      );
      event.request.url = scrubUrl(event.request.url);
      event.request.query_string = scrubQueryString(
        typeof event.request.query_string === "string" ? event.request.query_string : undefined,
      );
      // Drop request bodies entirely by default. Allow-list specific safe
      // fields in a follow-up if a debugging workflow needs them.
      delete event.request.data;
    }
    if (event.user) {
      delete event.user.email;
      delete event.user.username;
      delete event.user.ip_address;
    }
  } catch {
    // Best-effort scrub. Never block delivery on a scrubbing bug.
  }
  return event;
};

/**
 * Map a request path to a feature-area tag for the internal admin dashboard.
 * Buckets are coarse on purpose — they're for filtering and routing.
 */
export function inferFeatureArea(path: string | undefined): string {
  if (!path) return "internal_dashboard";
  const p = path.toLowerCase();

  if (p.includes("/dlq")) return "dlq";
  if (p.includes("/companies") || p.includes("/company")) return "companies";
  if (p.includes("/agent_runs") || p.includes("/agent-runs")) return "agent_runs";
  if (p.includes("/user") || p.includes("/invitation") || p.includes("/auth")) return "auth";

  return "internal_dashboard";
}

/**
 * Set the Sentry user from internal IDs only.
 * Never pass email, username, or IP — see plan §2.3.
 */
export function setSentryUser(user: { id: string; companyId?: string | null } | null): void {
  if (!user) {
    Sentry.setUser(null);
    return;
  }
  Sentry.setUser({
    id: user.id,
    company_id: user.companyId ?? undefined,
  });
}

export interface BffCaptureContext {
  requestId?: string;
  path: string;
  method: string;
  statusCode?: number;
  featureArea?: string;
}

/**
 * Capture an error from a BFF route handler (inside withRequestLogging).
 * Skips capture if the error has already been handled by ApiClient — see
 * shouldSkipBffCapture. Sets __sentry_captured__ to prevent further
 * downstream layers from double-capturing.
 */
export function captureBffError(error: unknown, context: BffCaptureContext): void {
  if (shouldSkipBffCapture(error)) return;

  Sentry.withScope((scope) => {
    scope.setTag("service", SERVICE_NAME);
    scope.setTag("source", "bff");
    if (context.requestId) scope.setTag("request_id", context.requestId);
    scope.setTag("path", context.path);
    scope.setTag("method", context.method);
    scope.setTag("feature_area", context.featureArea ?? inferFeatureArea(context.path));
    if (context.statusCode !== undefined) {
      scope.setTag("status_code", String(context.statusCode));
    }
    scope.setContext("bff_request", {
      path: context.path,
      method: context.method,
      status_code: context.statusCode,
      request_id: context.requestId,
    });
    Sentry.captureException(error, {
      mechanism: { handled: true, type: "bff" },
    });
  });

  markCaptured(error);
}

export interface ApiClientCaptureContext {
  requestId?: string;
  backendRequestId?: string;
  endpoint: string;
  method: string;
  status: number;
  featureArea?: string;
}

/**
 * Capture an error from ApiClient.request when a backend HTTP call fails.
 * Skips user-fault 4xx by default. Marks the error as captured so the BFF
 * wrapper does not re-capture it.
 */
export function captureApiClientError(error: unknown, context: ApiClientCaptureContext): void {
  // User-fault 4xx: do not capture. These dominate volume without offering signal.
  const SKIP_STATUSES = new Set([400, 401, 403, 404, 409, 422]);
  if (SKIP_STATUSES.has(context.status)) {
    markCaptured(error);
    return;
  }

  if (isCaptured(error)) return;

  Sentry.withScope((scope) => {
    scope.setTag("service", SERVICE_NAME);
    scope.setTag("source", "api_client");
    if (context.requestId) scope.setTag("request_id", context.requestId);
    if (context.backendRequestId) scope.setTag("backend_request_id", context.backendRequestId);
    scope.setTag("endpoint", context.endpoint);
    scope.setTag("method", context.method);
    scope.setTag("status_code", String(context.status));
    scope.setTag("feature_area", context.featureArea ?? inferFeatureArea(context.endpoint));
    scope.setContext("api_client_request", {
      endpoint: context.endpoint,
      method: context.method,
      status: context.status,
      request_id: context.requestId,
      backend_request_id: context.backendRequestId,
    });
    Sentry.captureException(error, {
      mechanism: { handled: true, type: "api_client" },
    });
  });

  markCaptured(error);
}

/**
 * Returns true if the BFF wrapper should skip capturing this error
 * because it has already been captured upstream (e.g. by ApiClient).
 */
export function shouldSkipBffCapture(error: unknown): boolean {
  return isCaptured(error);
}
